import { FC, useState } from 'react';
import { LegalDocumentResponseModel } from '@next/backend-client';
import { InformationCard, Loader, Space, Text } from '@kl/components-v6';
import {
    StyledContentWrapper,
    StyledLicenseAgreementsContainer,
    StyledLocalizedHeading,
    StyledSidebar,
    StyledTextContainer,
} from './styled';
import { LocalizedButton } from 'shared/ui/localized-button';
import { KNextIcon } from 'shared/ui/icons';
import { useLocalizationContext } from 'shared/lib/localization';

import { useTrackPageView } from 'features/tracking';
import { NoLegalDocument } from './NoLegalDocument';
import { NoLegalDocuments } from './NoLegalDocuments';
import { useLegalDocumentsQuery } from 'features/legal-documents';
import { useInitialInfo } from 'shared/api/use-initial-info';

export const LicenseAgreements: FC = () => {
    useTrackPageView('landing > legal-documents');
    const { isKscMode } = useInitialInfo();

    const { documents = [], isError, isFetching } = useLegalDocumentsQuery();

    const {
        localizationService: { get },
    } = useLocalizationContext();

    const [selectedDocument, setSelectedDocument] = useState<LegalDocumentResponseModel | null>(
        null,
    );

    const handleShowAgreement = (document: LegalDocumentResponseModel) =>
        setSelectedDocument(document);
    const handleHideAgreement = () => setSelectedDocument(null);

    if (isFetching) {
        return (
            <StyledLicenseAgreementsContainer height="100%">
                <StyledLocalizedHeading localizationKey="Licenses.AgreementsHeader" type="H5" />
                <Loader centered size="large" />
            </StyledLicenseAgreementsContainer>
        );
    }

    if (isError) {
        return (
            <StyledLicenseAgreementsContainer height="100%">
                <StyledLocalizedHeading localizationKey="Licenses.AgreementsHeader" type="H5" />
                <NoLegalDocuments />
            </StyledLicenseAgreementsContainer>
        );
    }

    return (
        <StyledLicenseAgreementsContainer>
            <StyledLocalizedHeading localizationKey="Licenses.AgreementsHeader" type="H5" />
            <Space align="flex-start" direction="vertical" gap={20} width="100%">
                {documents?.map(document => (
                    <InformationCard
                        key={document.Type}
                        leftSide={<KNextIcon />}
                        size="medium"
                        title={get(
                            isKscMode
                                ? `Licenses.LegalDocumentType.${document.Type}`
                                : `Licenses.NextLegalDocumentType.${document.Type}`,
                        )}
                        type="horizontal"
                        footer={
                            <LocalizedButton
                                localizationKey="Licenses.ViewAgreementButtonText"
                                mode="secondary"
                                onClick={() => handleShowAgreement(document)}
                            />
                        }
                    >
                        <StyledContentWrapper>
                            <Text color="secondary">
                                <div
                                    key={document.Type}
                                    dangerouslySetInnerHTML={{ __html: document.Text ?? '' }}
                                />
                            </Text>
                        </StyledContentWrapper>
                    </InformationCard>
                ))}
                <StyledSidebar
                    flex
                    title={get(
                        isKscMode
                            ? `Licenses.LegalDocumentType.${selectedDocument?.Type}`
                            : `Licenses.NextLegalDocumentType.${selectedDocument?.Type}`,
                    )}
                    visible={!!selectedDocument}
                    footer={
                        <LocalizedButton
                            localizationKey="Licenses.OkButtonText"
                            onClick={handleHideAgreement}
                        />
                    }
                    onClose={handleHideAgreement}
                >
                    <StyledTextContainer>
                        {selectedDocument?.Text ? (
                            <Text color="secondary">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: selectedDocument?.Text || '',
                                    }}
                                />
                            </Text>
                        ) : (
                            <NoLegalDocument />
                        )}
                    </StyledTextContainer>
                </StyledSidebar>
            </Space>
        </StyledLicenseAgreementsContainer>
    );
};
